import { useEffect } from "react";
import { createPortal } from "react-dom";
import { Printer, X } from "../../components/icons";
import { dateTimeLao, duration, fullLAK, num } from "../../lib/format";
import { isIdle } from "../../lib/dx";
import type { DashCtx } from "./ctx";

/**
 * ເບິ່ງຕົວຢ່າງລາຍງານ (mockup `dx_report.js`) — ใบสรุป A4 จากตัวเลขชุดเดียวกับจอ
 * พิมพ์/บันทึก PDF ผ่านหน้าต่างพิมพ์ของเบราว์เซอร์ (ตอนเปิดอยู่ พิมพ์เฉพาะใบนี้ — class บน body)
 */
export function ReportPreview({ ctx, onClose }: { ctx: DashCtx; onClose: () => void }) {
  const { here, data, unit } = ctx;
  const s = data.summary.data;
  const types = [...(data.docTypes.data ?? [])].sort((a, b) => b.requests - a.requests);
  const rows = [...(unit?.rows ?? [])].sort((a, b) => b.requests - a.requests);
  const needsChanges = data.statuses.data?.find((c) => c.status === "needs_changes")?.count ?? 0;

  useEffect(() => {
    document.body.classList.add("print-report");
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.classList.remove("print-report");
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  // วางที่ body (นอก .app-shell) — ตอนพิมพ์ซ่อนทั้ง shell เหลือแค่ใบรายงาน
  return createPortal(
    <div className="report-overlay" role="dialog" aria-modal="true" aria-label="ຕົວຢ່າງລາຍງານ">
      <div className="report-overlay__bar no-print">
        <strong>ຕົວຢ່າງລາຍງານ</strong>
        <span className="dx-sec__spacer" />
        <button type="button" className="btn-primary" onClick={() => window.print()}>
          <Printer size={14} /> ພິມ / ບັນທຶກ PDF
        </button>
        <button type="button" className="btn-secondary" onClick={onClose}>
          <X size={14} /> ປິດ
        </button>
      </div>
      <article className="report-sheet">
        <header className="report-sheet__head">
          <p className="report-sheet__kicker">ສາທາລະນະລັດ ປະຊາທິປະໄຕ ປະຊາຊົນລາວ</p>
          <h1>ລາຍງານສະຫຼຸບການບໍລິການ — {here.name}</h1>
          <p>
            ຊ່ວງເວລາ: <span className="num">{ctx.period}</span> · ຂໍ້ມູນເວລາ{" "}
            <span className="num">
{dateTimeLao(data.summary.dataUpdatedAt || 0)}
            </span>
          </p>
        </header>

        <h2>1. ຕົວເລກຫຼັກ</h2>
        <table className="report-table">
          <tbody>
            <tr><td>ຈຳນວນຄຳຮ້ອງ</td><td className="num">{s ? num(s.submitted) : "—"}</td></tr>
            <tr><td>ເອກະສານທີ່ອອກແລ້ວ</td><td className="num">{s ? num(s.issued) : "—"}</td></tr>
            {ctx.finance ? (
              <tr><td>ຄ່າທຳນຽມທີ່ຊຳລະແລ້ວ</td><td className="num">{s ? `${fullLAK(s.declared_fee_lak)} (${num(s.payments)} ລາຍການ)` : "—"}</td></tr>
            ) : null}
            <tr><td>ເວລາສະເລ່ຍ ຍື່ນ → ອອກເອກະສານ</td><td className="num">{s ? duration(s.avg_processing_seconds) : "—"}</td></tr>
            <tr><td>ກຳລັງດຳເນີນການ (ຕອນນີ້)</td><td className="num">{s ? num(s.pending) : "—"}</td></tr>
            <tr><td>ຄ້າງເກີນ 7 ມື້ (ຕອນນີ້)</td><td className="num">{s ? num(s.overdue_over_7d) : "—"}</td></tr>
            <tr><td>ຖືກສົ່ງຄືນໃຫ້ແກ້ໄຂ (ຕອນນີ້)</td><td className="num">{num(needsChanges)}</td></tr>
          </tbody>
        </table>

        {unit && rows.length > 0 ? (
          <>
            <h2>2. ສະຫຼຸບແຍກຕາມ{unit.word}</h2>
            <table className="report-table report-table--grid">
              <thead>
                <tr>
                  <th>{unit.word}</th>
                  <th>ຄຳຮ້ອງ</th>
                  <th>ອອກແລ້ວ</th>
                  {ctx.finance ? <th>ຄ່າທຳນຽມ (ກີບ)</th> : null}
                  <th>ເວລາສະເລ່ຍ</th>
                  <th>ຄ້າງເກີນ 7 ມື້</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.place_id ?? "unplaced"}>
                    <td>{unit.nameOf(r)}</td>
                    {isIdle(r) ? (
                      <td colSpan={ctx.finance ? 5 : 4}>ຍັງບໍ່ເປີດໃຊ້</td>
                    ) : (
                      <>
                        <td className="num">{num(r.requests)}</td>
                        <td className="num">{num(r.issued)}</td>
                        {ctx.finance ? <td className="num">{num(r.declared_fee_lak)}</td> : null}
                        <td className="num">{duration(r.avg_processing_seconds)}</td>
                        <td className="num">{num(r.overdue_over_7d)}</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
              {s ? (
                <tfoot>
                  <tr>
                    <td>ລວມ</td>
                    <td className="num">{num(s.submitted)}</td>
                    <td className="num">{num(s.issued)}</td>
                    {ctx.finance ? <td className="num">{num(s.declared_fee_lak)}</td> : null}
                    <td className="num">{duration(s.avg_processing_seconds)}</td>
                    <td className="num">{num(s.overdue_over_7d)}</td>
                  </tr>
                </tfoot>
              ) : null}
            </table>
          </>
        ) : null}

        <h2>{unit ? "3" : "2"}. ບໍລິການທີ່ຖືກຮ້ອງຂໍຫຼາຍທີ່ສຸດ</h2>
        {types.length === 0 ? (
          <p>ຍັງບໍ່ມີຄຳຮ້ອງໃນຊ່ວງນີ້</p>
        ) : (
          <table className="report-table report-table--grid">
            <thead>
              <tr>
                <th>ລະຫັດ</th>
                <th className="is-l">ບໍລິການ</th>
                <th>ຄຳຮ້ອງ</th>
                <th>ອອກແລ້ວ</th>
              </tr>
            </thead>
            <tbody>
              {types.map((d) => (
                <tr key={d.document_type_id}>
                  <td className="num is-l">{d.code}</td>
                  <td>{d.name_lo}</td>
                  <td className="num">{num(d.requests)}</td>
                  <td className="num">{num(d.issued)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <p className="report-sheet__foot">
          ຈຳນວນຄຳຮ້ອງນັບຕາມວັນທີ່ຍື່ນ · ຄ່າທຳນຽມນັບຕາມວັນທີ່ຊຳລະ · ສ້າງຈາກລະບົບ SDDS ADMIN
        </p>
      </article>
    </div>,
    document.body,
  );
}
